import React, { useRef, useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { PlayCircleFilled } from '@ant-design/icons';

import { Drawer } from '@/components/ui/Drawer';
import { formatDate } from '@/components/ui/Modals/utils';
import { useGetSelectedVideos, useGetVideoUrl } from './hooks';
import * as S from './styles';

interface Video {
	id: string;
	original_name: string;
	generated_name: string;
	created_at: string;
}

interface VideoDrawerProps {
	open?: boolean;
	onClose: () => void;
	onVideoSelect?: (video: Video) => void;
}

interface VideoPlayerProps {
	video: Video;
	onVideoSelect: (video: Video) => void;
}

function VideoPlayer({ video, onVideoSelect }: Readonly<VideoPlayerProps>) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const videoUrl = useGetVideoUrl(video);
	const [isPlaying, setIsPlaying] = useState(false);

	function handleVideoClick() {
		if (videoRef.current) {
			if (videoRef.current.paused) {
				videoRef.current.play();
			} else {
				videoRef.current.pause();
			}
		}
		onVideoSelect(video);
	}

	function handleOnPlay(): void {
		setIsPlaying(true);
	}

	function handleOnPause(): void {
		setIsPlaying(false);
	}

	function handleOnEnded(): void {
		setIsPlaying(false);
	}

	return (
		<S.VideoPlayer onClick={handleVideoClick}>
			<S.VideoThumbnail>
				{videoUrl ? (
					<video
						width="100%"
						height="100%"
						ref={videoRef}
						preload="metadata"
						title={video.original_name}
						style={{ width: '100%', height: '100%', objectFit: 'cover' }}
						controls={true}
						onPlay={handleOnPlay}
						onPause={handleOnPause}
						onEnded={handleOnEnded}
					>
						<source src={videoUrl} type="video/mp4" />
						<source src={videoUrl} type="video/webm" />
						Your browser does not support the video tag.
					</video>
				) : (
					<div
						style={{
							width: '100%',
							height: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							backgroundColor: '#f5f5f5',
							color: '#999'
						}}
					>
						Loading video...
					</div>
				)}
				{!isPlaying && (
					<S.PlayButton style={{ fontSize: '48px', color: '#002FBB' }}>
						<PlayCircleFilled />
					</S.PlayButton>
				)}
			</S.VideoThumbnail>
		</S.VideoPlayer>
	);
}

export function VideoDrawer({ open = false, onClose, onVideoSelect }: Readonly<VideoDrawerProps>) {
	function handleVideoSelect(video: Video): void {
		if (onVideoSelect) {
			onVideoSelect(video);
		}
	}

	const videos = useGetSelectedVideos();

	return (
		<Drawer open={open} onClose={onClose} gutter={[16, 16]} title={I18n.get('Video')}>
			<S.VideoContainer>
				{videos.map((video, index) => (
					<React.Fragment key={video.id}>
						<VideoPlayer video={video} onVideoSelect={handleVideoSelect} />
						<S.InfoItem>
							<S.InfoLabel>{I18n.get('File')}</S.InfoLabel>
							<S.InfoValue>{video.original_name}</S.InfoValue>
						</S.InfoItem>
						<S.InfoItem>
							<S.InfoLabel>{I18n.get('Uploaded date')}</S.InfoLabel>
							<S.InfoValue>{formatDate(video.created_at)}</S.InfoValue>
						</S.InfoItem>
						{index < videos.length - 1 && <S.Divider />}
					</React.Fragment>
				))}
			</S.VideoContainer>
		</Drawer>
	);
}
